import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import InfluencerProtectedRoute from './InfluencerProtectedRoute';
import Navbar from '../../components/Navbar';
import { useInfluencer } from '../../marketplace/hooks/useInfluencer';
import PricingPage from '../../modules/pricing/PricingPage';


// Auth Components
import InfluencerLogin from '../auth/Login';
import InfluencerForgotPassword from '../auth/ForgotPassword';
import InfluencerResetPassword from '../auth/ResetPassword';
import AuthCallback from '../auth/AuthCallback';

// Layout Component (Sidebar + Outlet)
import InfluencerSidebar from '../dashboard/InfluencerSidebar';

// Profile & Onboarding
import UserDetails from '../pages/UserDetails';
import InfluencerProfile from '../pages/InfluencerProfile';
import InstagramInsights from '../pages/InstagramInsights';
import Insights from '../pages/Insights';
import Media from '../pages/Media';

// Dashboard & Affiliate
import Dashboard from '../dashboard/Dashboard';
import ConnectSocialMedia from '../pages/ConnectSocialMedia';
import AffiliateLink from '../affiliateLink/AffiliateLink';
import ShareLinks from '../affiliateLink/ShareLinks';
import AffiliateDashboard from '../affiliateLink/AffiliateDashboard';
import YouTubeMediaDashboard from '../affiliateLink/YouTubeMediaDashboard';
import InstaMediaDashboard from '../pages/InstaMediaDashboard';
import InstagramAutomation from '../automations/pages/InstagramAutomation';
import AutomationDashboard from '../automations/pages/AutomationDashboard';
import AutomationDetails from '../automations/pages/AutomationDetails';
import ServicePayment from '../pages/ServicePayment';
import MyApplications from '../pages/MyApplications';
import Earnings from '../affiliateLink/Earnings';
import BankAccount from '../pages/BankAccount';
import LinkManagement from '../pages/LinkManagement';
import PaymentHistory from '../affiliateLink/PaymentsHistory';
import AcceptInvite from '../affiliateLink/AcceptInvite';
import FeatureGate from '../../modules/influencer-subscription/components/FeatureGate';
import { INFLUENCER_FEATURES } from '../../modules/influencer-subscription/config/influencerFeatureAccess.config';

// Settings & Account
import Settings from '../pages/Settings';
import SubscriptionBilling from '../pages/SubscriptionBilling';
import DeleteAccount from '../pages/DeleteAccount';
import { INFLUENCER_BASE } from '../constants';

export { INFLUENCER_BASE };

const MarketplaceProfileGate = () => {
  const { username } = useParams();
  const { influencer, loading } = useInfluencer(username);
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-800" />
      </div>
    );
  }
  if (influencer) return <PricingPage />;
  return <Navigate to={`${INFLUENCER_BASE}/dashboard`} replace />;
};

const InfluencerRoutes = () => {
  return (
    <>
      <Navbar />
      <Routes>
        {/* Public Auth Routes */}
        <Route path="login" element={<InfluencerLogin />} />
        <Route path="forgot-password" element={<InfluencerForgotPassword />} />
        <Route path="reset-password/:token" element={<InfluencerResetPassword />} />
        <Route path="auth/callback" element={<AuthCallback />} />

        {/* Standalone Protected Routes (no sidebar) */}
        <Route
          path="user-details"
          element={
            <InfluencerProtectedRoute>
              <UserDetails />
            </InfluencerProtectedRoute>
          }
        />
        <Route
          path="influencer-profile"
          element={<Navigate to={`${INFLUENCER_BASE}/dashboard/influencer-profile`} replace />}
        />
        <Route
          path="instagram-insights"
          element={
            <InfluencerProtectedRoute>
              <InstagramInsights />
            </InfluencerProtectedRoute>
          }
        />
        <Route
          path="insights"
          element={
            <InfluencerProtectedRoute>
              <Insights />
            </InfluencerProtectedRoute>
          }
        />
        <Route
          path="media"
          element={
            <InfluencerProtectedRoute>
              <Media />
            </InfluencerProtectedRoute>
          }
        />
        <Route
          path="payment-history"
          element={
            <InfluencerProtectedRoute>
              <PaymentHistory />
            </InfluencerProtectedRoute>
          }
        />
        <Route
          path="accept-invite/:token"
          element={
            <InfluencerProtectedRoute>
              <AcceptInvite />
            </InfluencerProtectedRoute>
          }
        />
        {/* Protected Dashboard Routes (with Sidebar Layout – uses Outlet) */}
        <Route
          path="dashboard"
          element={
            <InfluencerProtectedRoute>
              <InfluencerSidebar />
            </InfluencerProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="overview" element={<Dashboard />} />
          <Route path="connect-social-media" element={<ConnectSocialMedia />} />
          <Route path="generate-link" element={<FeatureGate feature={INFLUENCER_FEATURES.AFFILIATE_LINK}><AffiliateLink /></FeatureGate>} />
          <Route path="view-link" element={<ShareLinks />} />
          <Route path="affiliate-dashboard" element={<AffiliateDashboard />} />
          <Route path="youtube-media-dashboard" element={<YouTubeMediaDashboard />} />
          <Route path="insta-media-dashboard" element={<InstaMediaDashboard />} />
          <Route path="instagram-automation" element={<FeatureGate feature={INFLUENCER_FEATURES.AUTOMATION_DM}><InstagramAutomation /></FeatureGate>} />
          <Route path="instagram-automation/:id" element={<FeatureGate feature={INFLUENCER_FEATURES.AUTOMATION_DM}><InstagramAutomation /></FeatureGate>} />
          <Route path="automation-dashboard" element={<FeatureGate feature={INFLUENCER_FEATURES.AUTOMATION_DM}><AutomationDashboard /></FeatureGate>} />
          <Route path="automation-dashboard/:id" element={<FeatureGate feature={INFLUENCER_FEATURES.AUTOMATION_DM}><AutomationDetails /></FeatureGate>} />
          <Route path="service-payment" element={<ServicePayment />} />
          <Route path="my-applications" element={<MyApplications />} />
          <Route path="earnings" element={<Earnings />} />
          <Route path="bank" element={<BankAccount />} />
          <Route path="link-management" element={<FeatureGate feature={INFLUENCER_FEATURES.LINK_MANAGEMENT}><LinkManagement /></FeatureGate>} />
          <Route path="settings" element={<Settings />} />
          <Route path="influencer-profile" element={<InfluencerProfile />} />
          <Route path="subscription-billing" element={<SubscriptionBilling />} />
          <Route path="delete-account" element={<DeleteAccount />} />
        </Route>

        {/* Marketplace public profile: /influencer/:username */}
        <Route path=":username" element={<MarketplaceProfileGate />} />


        {/* Redirect root to login when not authenticated */}
        <Route path="" element={<Navigate to={`${INFLUENCER_BASE}/login`} replace />} />

        {/* Catch-all: redirect to dashboard */}
        <Route path="*" element={<Navigate to={`${INFLUENCER_BASE}/dashboard`} replace />} />
      </Routes>
    </>
  );
};

export default InfluencerRoutes;
